import { ButtonPresentationProps } from "@insite/mobius/Button/Props";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import { colorPalette, colorStrings, fontFamily, mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import { css } from "styled-components";

export const buttonCommon: ButtonPresentationProps = {
    shape: "rounded",
    activeMode: "lighten",
    hoverMode: "darken",
    sizeVariant: "large",
    typographyProps: {
        lineHeight: "normal",
        fontFamily,
        weight: 700,
        css: css`
            font-size: 16px;
            line-height: 22px;
            letter-spacing: 0;
            text-align: left;

            ${mediaQueriesBP.minMD} {
                font-size: 18px;
                line-height: 25px;
            }
        `,
    },
    css: css`
        height: 40px;
        min-height: 40px;
        transition: 0s;
        border-radius: 4px;

        ${mediaQueriesBP.minMD} {
            padding: 0 10px;
            height: auto;
            min-height: 50px;
        }
        ${mediaQueriesBP.minLG} {
            padding: 0 10px;
            height: auto;
            min-height: 50px;
        }

        &:hover {
            transition: 0.75s;
        }
    `,
    mergeCss: true,
};

export const buttonIconOnlyCommonStyles: ButtonPresentationProps = {
    variant: "primary",
    css: css`
        width: 24px;
        padding: 0;
        height: 24px;
        min-height: 24px;
        background-color: transparent;
        border: 0;

        &:hover,
        &:focus,
        &:disabled {
            ${IconWrapper} {
                color: ${getColor(colorStrings.commonGlobalWhite)};
                height: 100%;
                width: auto;
            }
        }

        ${mediaQueriesBP.minMD} {
            height: 24px;
            min-height: 24px;
            padding: 0;
        }

        ${IconWrapper} {
            top: 0;
            color: ${getColor(colorStrings.primaryMain)};
        }
    `,
    mergeCss: true,
};

export const removeButtonCompareButtonStyles: ButtonPresentationProps = {
    ...buttonIconOnlyCommonStyles,
    css: css`
        ${buttonIconOnlyCommonStyles.css};
        width: 20px;
        height: 20px;
        min-height: 20px;
        padding: 0;
        border-radius: 50%;
        border: 1px solid ${getColor(colorStrings.primaryMain)};

        ${mediaQueriesBP.minSM} {
            padding: 0;
            height: 16px;
            min-height: 16px;
            width: 16px;
            min-width: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
        }

        ${mediaQueriesBP.minMD} {
            height: 16px;
            min-height: 16px;
            width: 16px;
            min-width: 16px;
        }

        ${IconWrapper} {
            width: 100%;
            height: auto;
            top: 0;
            text-align: center;

            svg {
                height: auto;
                width: 100%;
            }
        }
    `,
};

export const button: {
    defaultProps?: ButtonPresentationProps;
    primary: ButtonPresentationProps;
    secondary: ButtonPresentationProps;
    tertiary: ButtonPresentationProps;
    primaryOnDark?: ButtonPresentationProps;
    secondaryOnDark?: ButtonPresentationProps;
} = {
    primary: {
        ...buttonCommon,
        color: colorStrings.primaryMain,
        css: css`
            border-radius: 4px;

            /*  create an outer line on focus */

            &:focus {
                background-color: ${getColor(colorStrings.commonPrimarySecond)};
                border-color: ${getColor(colorStrings.commonPrimarySecond)};
                color: ${getColor(colorStrings.commonGlobalWhite)};
                outline: solid 1px ${getColor(colorStrings.primaryMain)};
                outline-offset: 2px;
            }

            &:disabled {
                background-color: ${getColor(colorStrings.commonGlobalGray03)};
                color: ${getColor(colorStrings.commonGlobalWhite)};
                border-color: ${getColor(colorStrings.commonGlobalGray03)};
            }
        `,
        hoverStyle: {
            backgroundColor: `${getColor(colorStrings.commonPrimarySecond)}`,
            borderColor: `${getColor(colorStrings.commonPrimarySecond)}`,
            color: `${getColor(colorStrings.commonGlobalWhite)}`,
            outline: "none",
        },
        mergeCss: true,
    },
    secondary: {
        ...buttonCommon,
        color: colorStrings.primaryMain,
        buttonType: "outline",
        css: css`
            background: transparent;
            border-width: 1px;
            border-radius: 4px;

            &:hover {
                background-color: ${getColor(colorStrings.primaryMain)};
                border-color: ${getColor(colorStrings.primaryMain)};
                color: ${getColor(colorStrings.commonGlobalWhite)};
                outline: none;
            }

            /* create an outer line on focus */
            &:focus {
                outline: solid 1px ${getColor(colorStrings.primaryMain)};
                outline-offset: 2px;
                background-color: ${getColor(colorStrings.primaryMain)};
                border-color: ${getColor(colorStrings.primaryMain)};
                color: ${getColor(colorStrings.commonGlobalWhite)};
            }

            &:disabled {
                background-color: transparent;
                color: ${getColor(colorStrings.textDisabled)};
                border-color: ${getColor(colorStrings.commonGlobalGray03)};
            }
        `,
        hoverStyle: {
            borderColor: colorPalette.primary?.main,
            backgroundColor: colorPalette.primary?.main,
            color: (colorPalette.common as any).global.white,
        },
        mergeCss: true,
    },
    tertiary: {
        ...buttonCommon,
        color: colorStrings.commonGlobalWhite,
        buttonType: "outline",
        css: css`
            background: transparent;
            border: 1px solid ${getColor(colorStrings.commonGlobalWhite)};
            border-radius: 4px;

            &:hover,
            &:not(:disabled):hover {
                background-color: ${getColor(colorStrings.commonGlobalWhite)};
                border-color: ${getColor(colorStrings.commonGlobalWhite)};
                color: ${getColor(colorStrings.primaryMain)};
                outline: none;
            }

            /* create an outer line on focus */
            &:focus {
                outline: solid 1px ${getColor(colorStrings.commonGlobalWhite)};
                outline-offset: 2px;
            }

            &:disabled {
                background-color: transparent;
                color: ${getColor(colorStrings.commonGlobalGray03)};
                border-color: ${getColor(colorStrings.commonGlobalGray03)};
            }
        `,
        hoverStyle: {
            borderColor: colorPalette.primary?.main,
            backgroundColor: colorPalette.primary?.main,
            color: (colorPalette.common as any).global.white,
        },
        mergeCss: true,
    },

    primaryOnDark: {
        ...buttonCommon,
        color: colorStrings.commonGlobalWhite,
        css: css`
            color: ${getColor(colorStrings.textMain)};
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border-color: transparent;

            &:focus {
                background-color: ${getColor(colorStrings.secondaryMain)};
                outline: none;
            }

            &:hover {
                background-color: ${getColor(colorStrings.secondaryMain)};
            }

            &:disabled {
                background-color: ${getColor(colorStrings.commonGlobalGray04)};
                color: ${getColor(colorStrings.commonGlobalWhite)};
            }
        `,
    },
    secondaryOnDark: {
        ...buttonCommon,
        color: colorStrings.commonGlobalWhite,
        buttonType: "outline",
        activeStyle: {
            color: colorStrings.commonGlobalWhite,
        },
        hoverStyle: {
            backgroundColor: (colorPalette.common as any).global.white,
            color: colorStrings.textMain,
            borderColor: (colorPalette.common as any).global.white,
        },
        css: css`
            background: transparent;
            border-width: 1px;

            &:focus {
                outline: none;
                background-color: ${getColor(colorStrings.commonGlobalWhite)};
                border-color: ${getColor(colorStrings.commonGlobalWhite)};
                color: ${getColor(colorStrings.textMain)};
            }

            &:disabled {
                border-color: ${getColor(colorStrings.commonGlobalGray04)};
                background-color: ${getColor(colorStrings.commonGlobalGray04)};
                color: ${getColor(colorStrings.commonGlobalWhite)};
            }
        `,
    },
};
